import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { DataSource } from '@angular/cdk/collections';
import { MatDialog, MatPaginator, MatSort } from '@angular/material';
import { Observable } from 'rxjs/Observable';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import 'rxjs/add/operator/startWith';
import 'rxjs/add/observable/merge';
import 'rxjs/add/observable/fromEvent';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/debounceTime';
import { SweetAlertService } from '../../../../services/sweet-alert/sweet-alert.service';
import { fuseAnimations } from '../../../../../core/animations';
import { AuthenticationService } from '../../../../services/authentication/authentication.service';
import { TranslationService } from '../../../../services/translation/translation.service';
import { ReportService } from '../../../../services/reports/report.service';
import { ActivatedRoute, Router } from '@angular/router';
import { SchoolYearService } from '../../../../services/schoool-year/school-year.service';
import { SchoolYear } from '../../../../models/school-years/school-year';
import { environment } from '../../../../../../environments/environment';

@Component({
    selector: 'fuse-management-school-year-concept',
    templateUrl: './school-year-concept.component.html',
    styleUrls: ['./school-year-concept.component.scss'],
    animations: fuseAnimations
})
export class SchoolYearConceptComponent implements OnInit, OnDestroy {
    @BlockUI('tracker') blockUI: NgBlockUI;
    displayedColumns = ['Year', 'StartDate', 'FinishDate', 'Actions'];
    studentDatabase;
    dataSource: SchoolYearDataSource | null;

    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;
    @ViewChild('filter') filter: ElementRef;

    constructor(
        private activatedRoute: ActivatedRoute,
        private authenticationService: AuthenticationService,
        public dialog: MatDialog,
        private translationService: TranslationService,
        private sweetAlertService: SweetAlertService,
        private schoolYearService: SchoolYearService,
        private reportService: ReportService,
        private route: ActivatedRoute,
        private router: Router,
    ) {
    }

    ngOnInit() {
        this.blockUI.start(this.translationService.loading);
        this.studentDatabase = new SchoolYearDatabase(this.schoolYearService);
        this.dataSource = new SchoolYearDataSource(this.studentDatabase, this.paginator, this.sort);
        Observable.fromEvent(this.filter.nativeElement, 'keyup')
            .debounceTime(150)
            .distinctUntilChanged()
            .subscribe(() => {
                if (!this.dataSource) {
                    return;
                }
                this.dataSource.filter = this.filter.nativeElement.value;
            });
        this.blockUI.stop();
    }

    ngOnDestroy() {

    }

    edit(schoolYear: any) {
        this.router.navigate(['pages/management/professionalconcept/edit/' + schoolYear.Id]);
    }

    view(schoolYear: any) {
        this.router.navigate(['pages/management/professionalconcept/view/' + schoolYear.Id]);
    }

    printAll(schoolYear: any){
        window.open(environment.backendUri + 'report/teachingProfesionalConceptAll/' +  schoolYear.Id, '_new');
    }
}

export class SchoolYearDatabase {
    /** Stream that emits whenever the data has been modified. */
    dataChange: BehaviorSubject<SchoolYear[]> = new BehaviorSubject<SchoolYear[]>([]);

    get data(): SchoolYear[] {
        return this.dataChange.value;
    }

    constructor(
        private schoolYearService: SchoolYearService,
    ) {
        setTimeout(() => {
            this.schoolYearService.getAll().subscribe(response => {
                console.log('SchoolYearConcept > response', response);
                for (const r of response) {
                    this.add(r);
                }
            }, error => {

            });
        }, 100);
    }

    public add(store: SchoolYear) {
        const copiedData = this.data.slice();
        copiedData.push(store);
        this.dataChange.next(copiedData);
    }

    public edit(store: SchoolYear) {
        const copiedData = this.data.slice();
        const index = copiedData.findIndex(st => st.Id === store.Id);
        copiedData[index] = store;
        this.dataChange.next(copiedData);
    }

    public delete(store: SchoolYear) {
        const copiedData = this.data.slice();
        const index = copiedData.findIndex(st => st.Id === store.Id);
        copiedData.splice(index, 1);
        this.dataChange.next(copiedData);
    }
}

/**
 * Data source to provide what data should be rendered in the table. Note that the data source
 * can retrieve its data in any way. In this case, the data source is provided a reference
 * to a common data base, StoreDatabase. It is not the data source's responsibility to manage
 * the underlying data. Instead, it only needs to take the data and send the table exactly what
 * should be rendered.
 */
export class SchoolYearDataSource extends DataSource<any> {
    _filterChange = new BehaviorSubject('');

    get filter(): string {
        return this._filterChange.value;
    }

    set filter(filter: string) {
        this._filterChange.next(filter);
    }

    filteredData: SchoolYear[] = [];
    renderedData: SchoolYear[] = [];
    dataLength = 0;

    constructor(
        private _schoolYearDatabase: SchoolYearDatabase,
        private _paginator: MatPaginator,
        private _sort: MatSort
    ) {
        super();
        // Reset to the first page when the store changes the filter.
        this._filterChange.subscribe(() => this._paginator.pageIndex = 0);
    }

    /** Connect function called by the table to retrieve one stream containing the data to render. */
    connect(): Observable<SchoolYear[]> {
        // Listen for any changes in the base data, sorting, filtering, or pagination
        const displayDataChanges = [
            this._schoolYearDatabase.dataChange,
            this._sort.sortChange,
            this._filterChange,
            this._paginator.page
        ];

        return Observable.merge(...displayDataChanges).map(() => {
            // Filter data
            this.filteredData = this._schoolYearDatabase.data.slice().filter((item: SchoolYear) => {
                const searchStr = (item.Description).toLowerCase();
                return searchStr.indexOf(this.filter.toLowerCase()) !== -1;
            });

            // Sort filtered data
            const sortedData = this.sortData(this.filteredData.slice());

            // Grab the page's slice of the filtered sorted data.
            const startIndex = this._paginator.pageIndex * this._paginator.pageSize;
            this.renderedData = sortedData.splice(startIndex, this._paginator.pageSize);
            this.dataLength = this._schoolYearDatabase.data.length;
            return this.renderedData;
        });
    }

    disconnect() {
    }

    /** Returns a sorted copy of the database data. */
    sortData(data: SchoolYear[]): SchoolYear[] {
        if (!this._sort.active || this._sort.direction === '') {
            return data;
        }

        return data.sort((a, b) => {
            let propertyA: number | string = '';
            let propertyB: number | string = '';
            switch (this._sort.active) {
                case 'LastName':
                    [propertyA, propertyB] = [a.Description, b.Description];
                    break;
                // case '':
                //     [propertyA, propertyB] = [a.description, b.description];
                //     break;
            }

            const valueA = isNaN(+propertyA) ? propertyA : +propertyA;
            const valueB = isNaN(+propertyB) ? propertyB : +propertyB;

            return (valueA < valueB ? -1 : 1) * (this._sort.direction === 'asc' ? 1 : -1);
        });
    }
}



// WEBPACK FOOTER //
// ./src/app/main/content/pages/management/professional-concept/school-year-concept.component.ts